import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

// ── SUPABASE-BACKED PERSISTENCE ──────────────────────────────────────────────
// Estado local-first: localStorage da lectura instantánea y respaldo offline,
// mientras que Supabase guarda una copia remota en una tabla key-value
// `app_state(key text primary key, value jsonb, updated_at timestamptz)`.
// Si Supabase no está disponible (sin tabla / sin red), la app sigue
// funcionando sólo con localStorage — toda la sincronización es tolerante a
// fallos y nunca lanza errores hacia la UI.

const TABLE = 'app_state';

// Evita reintentos ruidosos si la tabla remota no existe / no es accesible.
let remoteDisabled = false;
async function remoteLoad<T>(key: string): Promise<T | undefined> {
  if (remoteDisabled) return undefined;
  try {
    const {
      data,
      error
    } = await supabase.from(TABLE).select('value').eq('key', key).maybeSingle();
    if (error) {
      // 42P01 = tabla inexistente; desactivamos remoto para no insistir.
      if ((error as {
        code?: string;
      }).code === '42P01') remoteDisabled = true;
      return undefined;
    }
    return data?.value as T ?? undefined;
  } catch {
    return undefined;
  }
}
async function remoteSave<T>(key: string, value: T): Promise<void> {
  if (remoteDisabled) return;
  try {
    const {
      error
    } = await supabase.from(TABLE).upsert({
      key,
      value,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'key'
    });
    if (error && (error as {
      code?: string;
    }).code === '42P01') {
      remoteDisabled = true;
    }
  } catch {

    /* offline u otro fallo: ignoramos, localStorage ya tiene la copia */}
}
export function useStoredState<T>(key: string, initial: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : initial;
    } catch {
      return initial;
    }
  });

  // Hidratar desde Supabase una sola vez al montar (sin pisar ediciones locales).
  const hydrated = useRef(false);
  useEffect(() => {
    let cancelled = false;
    remoteLoad<T>(key).then((remote) => {
      if (cancelled || remote === undefined) {
        hydrated.current = true;
        return;
      }
      // Sólo adoptamos el valor remoto si el usuario aún no ha tocado nada.
      if (!hydrated.current) {
        setValue(remote);
        try {
          window.localStorage.setItem(key, JSON.stringify(remote));
        } catch {

          /* ignore */}
      }
      hydrated.current = true;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persistir cambios en localStorage + Supabase (con debounce).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {

      /* ignore quota errors */}
    // No empujamos a remoto hasta haber intentado hidratar (evita carrera).
    if (!hydrated.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      remoteSave(key, value);
    }, 400);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [key, value]);
  return [value, setValue];
}
// ── SESIÓN DE USUARIO (solo local, con expiración) ───────────────────────────
// A diferencia de useStoredState, la sesión NUNCA se sincroniza con la tabla
// remota `app_state`: esa tabla es compartida por clave y sin distinción de
// dispositivo/usuario, así que si la sesión viajara por ahí, el último login
// de cualquier persona quedaría disponible para cualquier otro dispositivo
// que abriera la app (justo el problema de "entro desde el celular y ya
// estoy adentro"). La sesión vive únicamente en el localStorage del propio
// dispositivo/navegador, y además expira sola:
//  - por inactividad (nadie toca la app por SESSION_INACTIVITY_MS), y
//  - de forma absoluta (aunque haya actividad) a las SESSION_ABSOLUTE_MS.
const SESSION_KEY = 'getr.session';
const SESSION_INACTIVITY_MS = 30 * 60 * 1000; // 30 min sin uso -> fuera
const SESSION_ABSOLUTE_MS = 12 * 60 * 60 * 1000; // 12 h desde el login -> fuera

interface StoredSession<T> {
  usuario: T;
  loginAt: number;
  lastActivity: number;
}

function readSession<T>(): StoredSession<T> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession<T>;
  } catch {
    return null;
  }
}

function sessionExpired<T>(s: StoredSession<T>): boolean {
  const now = Date.now();
  return now - s.lastActivity > SESSION_INACTIVITY_MS || now - s.loginAt > SESSION_ABSOLUTE_MS;
}

export function useSessionState<T>(): [T | null, (usuario: T | null) => void] {
  const [usuario, setUsuario] = useState<T | null>(() => {
    const s = readSession<T>();
    if (!s) return null;
    if (sessionExpired(s)) {
      try {
        window.localStorage.removeItem(SESSION_KEY);
      } catch {
        /* ignore */
      }
      return null;
    }
    return s.usuario;
  });

  const setSession = (next: T | null) => {
    setUsuario(next);
    if (typeof window === 'undefined') return;
    try {
      if (next === null) {
        window.localStorage.removeItem(SESSION_KEY);
        return;
      }
      const existing = readSession<T>();
      const now = Date.now();
      const loginAt = existing ? existing.loginAt : now;
      window.localStorage.setItem(SESSION_KEY, JSON.stringify({
        usuario: next,
        loginAt,
        lastActivity: now
      }));
    } catch {
      /* ignore quota errors */
    }
  };

  // Mientras haya sesión: refresca "última actividad" con la interacción del
  // usuario, revisa periódicamente si expiró (inactividad o tiempo absoluto)
  // y se mantiene sincronizado si la sesión se cierra desde otra pestaña.
  useEffect(() => {
    if (typeof window === 'undefined' || !usuario) return;
    const touch = () => {
      const s = readSession<T>();
      if (!s) return;
      s.lastActivity = Date.now();
      try {
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(s));
      } catch {
        /* ignore */
      }
    };
    const checkExpiry = () => {
      const s = readSession<T>();
      if (!s || sessionExpired(s)) {
        try {
          window.localStorage.removeItem(SESSION_KEY);
        } catch {
          /* ignore */
        }
        setUsuario(null);
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_KEY && e.newValue === null) setUsuario(null);
    };
    const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    activityEvents.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
    window.addEventListener('storage', onStorage);
    const interval = setInterval(checkExpiry, 60 * 1000);
    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, touch));
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [usuario]);

  return [usuario, setSession];
}
export function csvCell(value: unknown): string {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}
export function downloadText(filename: string, contents: string, mime: string) {
  if (typeof window === 'undefined') return;
  const blob = new Blob([contents], {
    type: mime
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}